import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
  FormMessage,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  editPasswordSchema,
  EditPasswordFormData,
  EditPasswordFormFieldProps,
} from '../types';
import { TOAST_MESSAGES } from '../constants';
import { useUpdatePassword } from '../hooks/userSettingApi';
import { PasswordRequirementsSection } from '@/feature/authentication/components';
import { PasswordInput } from '@/components';

export const EditPasswordModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<EditPasswordFormData>({
    resolver: zodResolver(editPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const updatePasswordMutation = useUpdatePassword();
  const newPassword = form.watch('newPassword');

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    form.reset();
    form.clearErrors();
    setIsOpen(false);
  };

  const handleSubmit = (data: EditPasswordFormData) => {
    updatePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          toast.success(TOAST_MESSAGES.PASSWORD_UPDATE_SUCCESS);
          handleClose();
        },
        onError: (error: Error) => {
          console.error(error);
          // Handle specific field-level errors
          if (error.message === 'Password is incorrect.') {
            form.setError('currentPassword', {
              type: 'manual',
              message: 'Password is incorrect.',
            });
          } else if (error.message === 'Cannot use a previous password.') {
            form.setError('newPassword', {
              type: 'manual',
              message: 'Cannot use a previous password.',
            });
          } else {
            toast.error(TOAST_MESSAGES.PASSWORD_UPDATE_ERROR);
          }
        },
      },
    );
  };

  const hasValidationErrors = Object.keys(form.formState.errors).length > 0;

  // Check if all required fields are filled
  const formValues = form.watch();
  const allFieldsFilled =
    formValues.currentPassword &&
    formValues.newPassword &&
    formValues.confirmNewPassword;

  return (
    <>
      <Button variant="outline" onClick={handleOpen} aria-label="Edit password">
        <span className="text-sm font-medium">Edit</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="mx-auto max-w-md">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-xl font-semibold">
              Edit Password
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="currentPassword"
                render={({
                  field,
                }: EditPasswordFormFieldProps<'currentPassword'>) => (
                  <FormItem className="w-full">
                    <FormLabel>Current Password *</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder="Enter current password"
                        aria-label="Current Password"
                        showPasswordLabel="Show current password"
                        hidePasswordLabel="Hide current password"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({
                  field,
                }: EditPasswordFormFieldProps<'newPassword'>) => (
                  <FormItem className="w-full">
                    <FormLabel>New Password *</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder="Enter new password"
                        aria-label="New Password"
                        showPasswordLabel="Show new password"
                        hidePasswordLabel="Hide new password"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />

              {/* Password Requirements Indicator */}
              <PasswordRequirementsSection password={newPassword || ''} />

              <FormField
                control={form.control}
                name="confirmNewPassword"
                render={({
                  field,
                }: EditPasswordFormFieldProps<'confirmNewPassword'>) => (
                  <FormItem>
                    <FormLabel>Confirm New Password *</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder="Confirm new password"
                        aria-label="Confirm New Password"
                        showPasswordLabel="Show confirm password"
                        hidePasswordLabel="Hide confirm password"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />

              <DialogFooter className="items-center border-t pt-4">
                {hasValidationErrors && form.formState.isSubmitted && (
                  <FormMessage className="text-destructive">
                    Please fix validation errors
                  </FormMessage>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  aria-label="Cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  aria-label="Save"
                  disabled={
                    updatePasswordMutation.isPending ||
                    hasValidationErrors ||
                    !allFieldsFilled
                  }
                  loading={updatePasswordMutation.isPending}
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
